import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import { Theme, useTheme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import ImageTools from 'common/utils/ImageTools';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Grid from '@mui/material/Grid';
import classNames from 'classnames';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { RiDeleteBinLine } from 'react-icons/ri';
import Spinner from 'components/Spinner';
import { useTranslation } from 'services/localization/useTranslation';
import { newId } from 'common/utils/common';
import { api } from 'common/frontend/api';
import * as ShopSelectors from 'selectors/ShopSelectors';
import {
	storageRef,
	uploadBytes,
	getDownloadURL,
	deleteObject,
} from 'common/frontend/firebase/storage';
import { FieldValues } from 'common/frontend/firebase/firestore';
import { clearImageReferences } from './utils';

interface Props {
	isOpen: boolean;
	onCancel: () => void;
	onSave: (img: string | string[] | null) => void;
	img: string | string[] | null;
	shopId: string;
	isMultiImage?: boolean;
}

const ImageDialog = (props: Props) => {
	const { img, isMultiImage } = props;
	const [imgs, setImgs] = useState<string[] | null>(!img ? null : Array.isArray(img) ? img : [img]);
	const [loading, setLoading] = useState<boolean>(false);
	const [_error, setError] = useState<string>('');

	const { t } = useTranslation();
	const classes = useStyles();
	const theme = useTheme();
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const images = useSelector(ShopSelectors.activeShopImages);
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo);

	const xs = window.matchMedia('screen and (orientation:portrait) and (max-width: 600px)').matches;

	const handleCancel = () => {
		props.onCancel();
	};

	const handleOk = () => {
		const toSave = !imgs ? imgs : isMultiImage ? imgs : imgs[0];
		props.onSave(toSave);
	};

	const handleImageClick = (img: string | null) => {
		if (!img) {
			setImgs(null);
		} else if (isMultiImage) {
			const updatedUrls = imgs?.includes(img)
				? imgs.filter((i) => i !== img)
				: (imgs ?? []).concat(img);
			setImgs(updatedUrls);
		} else {
			setImgs([img]);
		}
	};

	const getThumbImage = (file: File): Promise<Blob> => {
		return new Promise((resolve) =>
			ImageTools.resize(
				file,
				{
					width: 1200, // maximum width
					height: 1200, // maximum height
				},
				(blob: any, didItResize: boolean) => {
					// didItResize will be true if it managed to resize it, otherwise false (and will return the original file as 'blob')
					resolve(blob);
					// document.getElementById('preview').src = window.URL.createObjectURL(blob);
					// you can also now upload this blob using an XHR.
				},
			),
		);
	};

	const uploadImage = async (args: { image: Blob; name: string; shopId: string }) => {
		setLoading(true);
		const { image, name, shopId } = args;
		const ref = storageRef();
		const imageRef = storageRef(ref, 'product-images/' + shopId + '/' + name);
		const imageUpload = await uploadBytes(imageRef, image);
		const imageUrl = await getDownloadURL(imageUpload.ref);

		await api()
			.shops.doc(shopId)
			.update({
				images: FieldValues.arrayUnion(imageUrl),
			});
		setLoading(false);
	};

	const deleteImage = async (args: { imageUrl: string; shopId: string }) => {
		const { imageUrl, shopId } = args;
		try {
			if (!imageUrl.includes('demo-shop-images')) {
				const imgReference = storageRef(imageUrl);
				deleteObject(imgReference)
					.then(() => {
						// File deleted successfully
					})
					.catch((error) => {
						// Uh-oh, an error occurred!
					});
			}
		} catch (err) {
		} finally {
			await api()
				.shops.doc(shopId)
				.update({ images: FieldValues.arrayRemove(imageUrl) });
		}
		clearImageReferences(imageUrl, shopPublicInfo);
	};

	const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (files) {
			// tslint:disable-next-line
			for (let i = 0; i < files.length; i++) {
				const file = files[i];
				if (!file.type.match(/image.*/)) {
					setError(t('imageDialog.wrongImageFormat', 'Wrong image format'));
					continue;
				}
				const fileName = `${file.name.replace(/\.[^/.]+$/, '')}_${newId(5)}`;
				// const largeImagePromise = this.getLargeImage(file);
				const thumbImagePromise = getThumbImage(file);
				// const largeImage = await largeImagePromise;
				const thumbImage = await thumbImagePromise;
				uploadImage({ image: thumbImage, name: fileName, shopId });
			}
		}
	};

	const handleDelete = async (e: any, imageUrl: string) => {
		e.preventDefault();
		e.stopPropagation();
		if (
			window.confirm(
				t('imageDialog.deleteConfirmation', 'Are you sure you want to delete the image?'),
			)
		) {
			await deleteImage({ imageUrl, shopId });

			if (imgs?.includes(imageUrl)) {
				setImgs(imgs.filter((i) => i !== imageUrl));
			}
		}
	};

	return (
		<Dialog
			disableEscapeKeyDown
			maxWidth={false}
			aria-labelledby="confirmation-dialog-title"
			open={props.isOpen}
			fullScreen={xs ? true : false}
			classes={{
				paper: classes.dialog,
			}}
		>
			<DialogTitle id="image-dialog">{t('imageDialog.chooseImage', 'Choose image')}</DialogTitle>
			<DialogContent>
				<ImageList rowHeight={160} gap={16} className={classes.gridList} cols={xs ? 2 : 3}>
					<ImageListItem
						cols={1}
						onClick={() => handleImageClick(null)}
						classes={{
							root: classNames(classes.tile, !imgs ? classes.selectedTile : {}),
							img: classes.tileImg,
						}}
					>
						<Typography>{t('imageDialog.noImage', 'No image')}</Typography>
					</ImageListItem>
					{images &&
						images.map((img, idx) => (
							<ImageListItem
								key={idx}
								cols={1}
								onClick={() => handleImageClick(img)}
								sx={{ objectFit: 'contain' }}
								classes={{
									root: classNames(classes.tile, imgs?.includes(img) ? classes.selectedTile : {}),
									img: classes.tileImg,
								}}
							>
								<img src={img} alt="" />
								{isMultiImage && imgs?.includes(img) && (
									<div
										className={classNames(
											classes.imageOrderNumber,
											classes.circleBg,
											classes.bgBlack,
										)}
									>
										<Typography
											variant="body1"
											fontWeight="bold"
											className={classes.imageOrderNumberText}
										>
											{imgs.indexOf(img) + 1}
										</Typography>
									</div>
								)}
								<div className={classNames(classes.iconContainer, classes.circleBg)}>
									<IconButton
										className={classes.icon}
										onClick={(e) => handleDelete(e, img)}
										size="large"
									>
										<RiDeleteBinLine color={theme.palette.primary.main} size={20} />
									</IconButton>
								</div>
							</ImageListItem>
						))}
					{loading && (
						<ImageListItem
							cols={1}
							classes={{
								root: classes.tile,
								img: classes.tileImg,
							}}
						>
							<Spinner in={true} />
						</ImageListItem>
					)}
				</ImageList>
			</DialogContent>
			<DialogActions className={classes.actionContainer}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} container justifyContent={xs ? 'center' : 'flex-start'}>
						<Button variant="text" color="primary" component="label">
							{t('imageDialog.uploadNew', 'Upload new image(s)')}
							<input
								onChange={(e) => handleUpload(e)}
								style={{ display: 'none' }}
								type="file"
								accept="image/*"
								multiple
							/>
						</Button>
					</Grid>
					<Grid item xs={12} sm={6} container justifyContent="flex-end">
						<Button onClick={handleCancel} color="secondary">
							{t('common:actions.cancel')}
						</Button>
						<Button onClick={handleOk} color="primary" variant="contained">
							{t('common:greetings.okBtn')}
						</Button>
					</Grid>
				</Grid>
			</DialogActions>
		</Dialog>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		dialog: {
			width: '100%',
			maxWidth: 955,
			maxHeight: '90vh',
			background: theme.palette.background.paper,
		},
		iconContainer: {
			position: 'absolute',
			top: 0,
			right: 0,
			margin: theme.spacing(0.5, 0.5, 0, 0),
			padding: theme.spacing(2),
		},
		circleBg: {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			width: '24px',
			height: '24px',
			borderRadius: '50%',
			backgroundColor: theme.palette.background.paper,
		},
		bgBlack: {
			backgroundColor: theme.palette.primary.main,
		},
		imageOrderNumber: {
			position: 'absolute',
			top: 0,
			left: 0,
			color: theme.palette.colors.tundra.main,
			fontWeight: 'bold',
			margin: theme.spacing(0.5, 0, 0, 0.5),
		},
		imageOrderNumberText: {
			fontSize: '1.25rem',
			color: theme.palette.primary.contrastText,
		},
		gridList: {
			width: '100%',
			height: 'auto',
			padding: 8,
		},
		tile: {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			cursor: 'pointer',
			border: `2px solid ${theme.palette.border.inputOutline}`,
		},
		tileImg: {
			maxWidth: '100%',
			maxHeight: '100%',
			width: 'auto',
			height: 'auto',
			transform: 'scale(1.0)',
			position: 'static',
			//@ts-ignore
			objectFit: 'contain !important',
		},
		selectedTile: {
			border: `2px solid ${theme.palette.primary.main}`,
			borderRadius: '2px',
		},
		actionContainer: {
			boxShadow: theme.shadows[15],
			padding: theme.spacing(4, 2),
			zIndex: 99,
		},
		icon: {
			padding: 4,
		},
	}),
);

export default ImageDialog;
